import { AB_ORDERS_REVAMP, HEADER_NAV_COLOUR } from 'constants/experiments';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { State } from 'shared/store/types';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { getPageType } from 'components/Header/helper';
import { IS_MEN } from 'shared/helpers';
import { HELP_CENTER, NEED_HELP, MY_ORDERS } from 'constants/strings';
import { IN_HOUSE_HELP_CENTER_URLS } from 'constants/urls';
import { useConfigSwitch } from 'hooks/switch';
import { IS_IN_HOUSE_HELP_CENTER_ENABLED_SWITCH } from 'store/remoteConfig/constants';
import { MenuItemOverride, MenuItems } from 'types/Header';
import { getMenuItems } from 'helpers/utils/kebabMenu';
import { getFilteredMenuItems } from 'shared/constants/kebabMenu';
import { userSessionTypes } from 'shared/constants';
import {
  MY_ORDERS_PARAMS,
  ORDERS_LINK,
  ORDERS_REVAMP_LINK,
} from 'layouts/MyProfile/constants';

export const useIsThemeHeader = () => {
  const { pageTypes = [] } = useSelector(
    (state: State) => state?.remoteConfig.configs.themeHeader || {}
  );
  const { pathname } = useLocation();
  const isThemeHeader = useSelector((state: State) =>
    getEligibleForVariant(state, HEADER_NAV_COLOUR, 'A')
  );
  const currentPagetType = getPageType(pathname);

  return isThemeHeader && pageTypes.includes(currentPagetType);
};

export const useOrdersUrl = () => {
  const ordersRedirectionExpEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_ORDERS_REVAMP, 'A')
  );
  const ordersRevampUrl = useSelector(
    (state: State) =>
      state.remoteConfig.configs?.ordersRevampUrl ||
      `${ORDERS_REVAMP_LINK}?${MY_ORDERS_PARAMS.SRC_PARAM}`
  );

  if (ordersRedirectionExpEnabled) {
    return ordersRevampUrl;
  }
  return `${ORDERS_LINK}?${MY_ORDERS_PARAMS.PTYPE_PARAM}`;
};

export const useMenuItems = (): MenuItems => {
  const { beauty: BEAUTY_HELP_CENTER_URL } = IN_HOUSE_HELP_CENTER_URLS;
  const isInHouseHelpCenterEnabled = useConfigSwitch(
    IS_IN_HOUSE_HELP_CENTER_ENABLED_SWITCH
  );
  const ordersUrl = useOrdersUrl();
  const kebabMenuConfig = useSelector(
    (state: State) => state.remoteConfig.switches.kebabMenu
  );

  const myOrdersRevampOverride = {
    [MY_ORDERS]: {
      link: ordersUrl,
      displayText: MY_ORDERS,
    },
  };

  const helpCenterOverride = {
    [HELP_CENTER]: {
      link: BEAUTY_HELP_CENTER_URL,
      displayText: HELP_CENTER,
    },
    [NEED_HELP]: {
      link: BEAUTY_HELP_CENTER_URL,
      displayText: HELP_CENTER,
    },
  };

  const overrides: MenuItemOverride = {
    ...(isInHouseHelpCenterEnabled && !IS_MEN && helpCenterOverride),
    ...myOrdersRevampOverride,
  };

  // First apply overrides, then apply filtering based on remote config
  const menuItemsMap = getMenuItems(overrides);

  // Apply remote config filtering
  const filteredMenuItems: MenuItems = {
    [userSessionTypes.GUEST]: getFilteredMenuItems(
      __PLATFORM__,
      userSessionTypes.GUEST,
      kebabMenuConfig
    ),
    [userSessionTypes.LOGGED_IN]: getFilteredMenuItems(
      __PLATFORM__,
      userSessionTypes.LOGGED_IN,
      kebabMenuConfig
    ),
  };

  // Merge overrides with filtered items
  const finalMenuItems: MenuItems = {
    [userSessionTypes.GUEST]: (
      filteredMenuItems[userSessionTypes.GUEST] || []
    ).map((item) => ({
      ...item,
      ...menuItemsMap[userSessionTypes.GUEST]?.find(
        (m) => m.displayText === item.displayText
      ),
    })),
    [userSessionTypes.LOGGED_IN]: (
      filteredMenuItems[userSessionTypes.LOGGED_IN] || []
    ).map((item) => ({
      ...item,
      ...menuItemsMap[userSessionTypes.LOGGED_IN]?.find(
        (m) => m.displayText === item.displayText
      ),
    })),
  };

  return finalMenuItems;
};
export const useIsConfigHeader = () => {
  const headerAndFooterBgConfig = useSelector(
    (state: State) => state.remoteConfig.configs.headerAndFooterBgConfig
  );
  const { headerBg, footerBg, headerTextColor } = headerAndFooterBgConfig || {};
  return {
    headerBg,
    footerBg,
    headerTextColor,
  };
};
